import { cn } from '@app/utils/cn';
import { useContainsActiveElement } from '@app/utils/hooks/useContainsActiveElement';
import { Switcher } from '@typings';
import React from 'react';
import { PlacementType } from 'react-laag/dist/PlacementType';

import { ContentSwitcherTab } from './ContentSwitcherTab';
import { useContentSwitcherKeyboardHandler } from './useContentSwitcherKeyboardHandler';

interface Props<T, OnChangeType> {
  tabs: Switcher.Tab<T>[];
  groupName: string;
  selectedValue: T;
  onChange: OnChangeType;
  variant?: Switcher.Variant;
  keepEvenWidth?: boolean;
  tooltipPlacement?: PlacementType;
  isDisabled?: boolean;
  className?: string;
  dataTestId?: string;
  defaultNestedOptions?: Record<string, string>;
}

export const ContentSwitcher = <T extends string | undefined, OnChangeType extends (selectedValue: T) => void>({
  tabs,
  groupName,
  selectedValue,
  onChange,
  variant = 'light',
  tooltipPlacement,
  isDisabled = false,
  className,
  dataTestId,
  defaultNestedOptions,
  keepEvenWidth = false,
}: Props<T, OnChangeType>) => {
  const ref = React.useRef<HTMLButtonElement>(null);

  const handleTabChange = (value: T) => {
    onChange(value);
  };

  const { currentFocusedValue, handleKeyDown } = useContentSwitcherKeyboardHandler({ onChange, selectedTabKey: selectedValue, tabs });
  const { containerRef, hasActiveElement } = useContainsActiveElement<HTMLDivElement>();

  React.useEffect(() => {
    if (hasActiveElement) {
      ref.current?.focus();
    }
  }, [currentFocusedValue, hasActiveElement]);

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={groupName}
      data-testid={dataTestId}
      className={cn(
        'flex h-8 rounded-large border border-neutral-90',
        {
          'cursor-not-allowed': isDisabled,
        },
        className,
      )}
      style={keepEvenWidth ? { display: 'grid', gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` } : {}}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      {tabs.map(tab => {
        const isSelected = currentFocusedValue === tab.value;

        return (
          <ContentSwitcherTab
            ref={isSelected ? ref : undefined}
            key={tab.value}
            tab={tab}
            groupName={groupName}
            isSelected={isSelected}
            onChange={handleTabChange}
            tooltipPlacement={tooltipPlacement}
            isDisabled={isDisabled}
            variant={variant}
            selectedTabOption={defaultNestedOptions?.[tab.value as string]}
          />
        );
      })}
    </div>
  );
};
